import React, { useCallback, useEffect, useRef, useSyncExternalStore } from "react"
import type { Howl } from "howler"

import { HowlStore } from "./players/howl/store"
import defaultState from "./players/state"
import { PcmStreamStore } from "./players/pcm/store"
import type { AudioControls, AudioLoadOptions, Snapshot, Store } from "./types"
import { UAParser } from "ua-parser-js"

// Define separate types for PCM and Howl options
type PcmLoadOptions = {
    src: string;
    autoplay?: boolean;
    loop?: boolean;
    initialVolume?: number;
    initialMute?: boolean;
    onload?: () => void;
    onplay?: () => void;
    onend?: () => void;
    onpause?: () => void;
    onstop?: () => void;
};

type HowlLoadOptions = {
    src: string;
    format?: string;
    html5?: boolean;
    autoplay?: boolean;
    loop?: boolean;
    initialVolume?: number;
    initialMute?: boolean;
    initialRate?: number;
    onload?: () => void;
    onplay?: () => void;
    onend?: () => void;
    onpause?: () => void;
    onstop?: () => void;
};

// Helper function to create PCM options
function createPcmOptions(
    src: string,
    options?: AudioLoadOptions
): PcmLoadOptions {  // Using 'any' to allow both string and ArrayBuffer src types
    return {
        src: src, // Pass the source directly, whether it's a string URL or ArrayBuffer
        autoplay: options?.autoplay,
        loop: options?.loop,
        initialVolume: options?.initialVolume,
        initialMute: options?.initialMute,
        onload: options?.onload,
        onplay: options?.onplay,
        onend: options?.onend,
        onpause: options?.onpause,
        onstop: options?.onstop
    };
}

// Helper function to create Howl options
function createHowlOptions(
    src: string,
    options?: AudioLoadOptions
): HowlLoadOptions {
    return {
        src,
        format: options?.format,
        html5: options?.html5,
        autoplay: options?.autoplay,
        loop: options?.loop,
        initialVolume: options?.initialVolume,
        initialMute: options?.initialMute,
        initialRate: options?.initialRate,
        // event callbacks
        onload: options?.onload,
        onplay: options?.onplay,
        onend: options?.onend,
        onpause: options?.onpause,
        onstop: options?.onstop
    };
}

/**
 * Returns true if the audio player needs to use a PCM player.
 * @param options - Audio load options that may indicate PCM stream processing
 * @returns Boolean indicating whether a PCM player is needed
 */
function needsPcmPlayer(options?: AudioLoadOptions): boolean {
    const isWebKit = new UAParser().getEngine().name === "WebKit";

    return !!(options?.isPCMStream) && isWebKit;
}

// Helper function to load audio based on source type
function loadAudio(
    howlerRef: React.MutableRefObject<Store>,
    pcmAudioRef: React.MutableRefObject<Store>,
    src: string,
    options?: AudioLoadOptions
): void {
    if (needsPcmPlayer(options)) {
        // Destroy the howlerRef if it exists
        if (howlerRef.current) {
            console.log("Destroying Howler instance for PCM stream");
            howlerRef.current.destroy();
        }
        // Destroy the pcmAudioRef if it exists
        if (pcmAudioRef.current) {
            console.log("Destroying PCM instance for Howl stream");
            pcmAudioRef.current.destroy();
        }
        const pcmOptions = createPcmOptions(src, options);
        (pcmAudioRef.current as PcmStreamStore).load(pcmOptions);
    } else {
        // Destroy the howlerRef if it exists
        if (howlerRef.current) {
            console.log("Destroying Howler instance for PCM stream");
            howlerRef.current.destroy();
        }
        // Destroy the pcmAudioRef if it exists
        if (pcmAudioRef.current) {
            console.log("Destroying PCM instance for Howl stream");
            pcmAudioRef.current.destroy();
        }

        const howlOptions = createHowlOptions(src, options);
        (howlerRef.current as HowlStore).load(howlOptions);
    }
}

export type AudioPlayer = AudioControls &
    Snapshot & {
        /** A reference to the underlying player object.
         * For regular audio, this is a Howl object.
         * */
        player: Howl | null
        src: string | null
        /** A way to explicitly load an audio resource */
        load: (...args: [string, AudioLoadOptions | undefined]) => void
        /** Removes event listeners, resets state and unloads the internal player object */
        cleanup: () => void,
        canSeek: () => boolean,
        canLoop: () => boolean,
        canChangeRate: () => boolean,
    }

export function useAudioPlayer(): AudioPlayer

/**
 */
export function useAudioPlayer() {
    const [isPcmPlayer, setIsPcmPlayer] = React.useState(false);

    // Choose the appropriate store based on the options
    const pcmAudioRef = useRef<Store>(new PcmStreamStore()) as React.MutableRefObject<Store>;
    const howlerRef = useRef<Store>(new HowlStore()) as React.MutableRefObject<Store>;

    // need to bind functions back to the howl since they will be called from the context of React
    const pcmAudioState = useSyncExternalStore(
        pcmAudioRef.current.subscribe.bind(pcmAudioRef.current),
        pcmAudioRef.current.getSnapshot.bind(pcmAudioRef.current),
        () => defaultState
    );
    const howlerState = useSyncExternalStore(
        howlerRef.current.subscribe.bind(howlerRef.current),
        howlerRef.current.getSnapshot.bind(howlerRef.current),
        () => defaultState
    );

    useEffect(() => {
        // cleans up the sound when hook unmounts
        return () => {
            if (pcmAudioRef.current) {
                pcmAudioRef.current.destroy()
            }
            if (howlerRef.current) {
                howlerRef.current.destroy()
            }
        }
    }, []);

    const load: AudioPlayer["load"] = useCallback((src, options) => {
        if (needsPcmPlayer(options)) {
            setIsPcmPlayer(true);
        }

        loadAudio(howlerRef, pcmAudioRef, src, options);
    }, [])

    const state = isPcmPlayer ? pcmAudioState : howlerState;
    const audioRef = isPcmPlayer ? pcmAudioRef : howlerRef;

    return {
        ...state,
        player: audioRef.current instanceof HowlStore ? audioRef.current.howl : null,
        src: audioRef.current.src,
        load: load,
        // AudioControls interface
        play: audioRef.current.play.bind(audioRef.current),
        pause: audioRef.current.pause.bind(audioRef.current),
        togglePlayPause: audioRef.current.togglePlayPause.bind(
            audioRef.current
        ),
        stop: audioRef.current.stop.bind(audioRef.current),
        setVolume: audioRef.current.setVolume.bind(audioRef.current),
        fade: audioRef.current.fade.bind(audioRef.current),
        mute: audioRef.current.mute.bind(audioRef.current),
        unmute: audioRef.current.unmute.bind(audioRef.current),
        toggleMute: audioRef.current.toggleMute.bind(audioRef.current),
        setRate: audioRef.current.setRate.bind(audioRef.current),
        seek: audioRef.current.seek.bind(audioRef.current),
        loopOn: audioRef.current.loopOn.bind(audioRef.current),
        loopOff: audioRef.current.loopOff.bind(audioRef.current),
        toggleLoop: audioRef.current.toggleLoop.bind(audioRef.current),
        getPosition: audioRef.current.getPosition.bind(audioRef.current),
        cleanup: audioRef.current.destroy.bind(audioRef.current),
        canSeek: () => audioRef.current instanceof HowlStore ? true : false,
        canLoop: () => audioRef.current instanceof HowlStore ? true : false,
        canChangeRate: () => audioRef.current instanceof HowlStore ? true : false,
    }
}
